import { useEffect, useRef, useState, useCallback } from 'react';
import SockJS from 'sockjs-client';
import { Client, IMessage } from '@stomp/stompjs';
import { debugLogger } from '../utils/debugLogger';

export interface QRStatusMessage {
  sessionId: string;
  status: 'PENDING' | 'SCANNED' | 'CONFIRMED' | 'EXPIRED' | 'CANCELLED';
  accessToken?: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
  user?: {
    userId: number;
    username: string;
    email?: string;
    displayName?: string;
    avatarUrl?: string;
  };
  timestamp: string;
}

export interface UseQRLoginWebSocketOptions {
  sessionId?: string;
  onStatusUpdate?: (message: QRStatusMessage) => void;
  onError?: (error: any) => void;
}

export const useQRLoginWebSocket = (options: UseQRLoginWebSocketOptions = {}) => {
  const { sessionId, onStatusUpdate, onError } = options;
  
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latestStatus, setLatestStatus] = useState<QRStatusMessage | null>(null);
  
  const clientRef = useRef<Client | null>(null);
  const subscriptionRef = useRef<any>(null);
  const currentSessionIdRef = useRef<string | undefined>(undefined);
  const callbacksRef = useRef({ onStatusUpdate, onError });
  const isConnectingRef = useRef(false);

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = { onStatusUpdate, onError };
  }, [onStatusUpdate, onError]);

  const connect = useCallback(() => {
    if (!sessionId) {
      debugLogger.log('useQRLoginWebSocket', 'No sessionId provided, skipping connection');
      return;
    }

    // Prevent multiple connections for the same sessionId
    if (currentSessionIdRef.current === sessionId && (isConnectingRef.current || isConnected)) {
      debugLogger.log('useQRLoginWebSocket', 'Already connected or connecting to this sessionId', { sessionId });
      return;
    }

    // Disconnect existing connection if sessionId changed
    if (clientRef.current && currentSessionIdRef.current !== sessionId) {
      debugLogger.log('useQRLoginWebSocket', 'SessionId changed, disconnecting old connection', {
        oldSessionId: currentSessionIdRef.current,
        newSessionId: sessionId
      });
      disconnect();
    }

    isConnectingRef.current = true;
    currentSessionIdRef.current = sessionId;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const wsUrl = `${apiUrl}/ws`;

    debugLogger.log('useQRLoginWebSocket', 'Connecting to WebSocket', { wsUrl, sessionId });

    const client = new Client({
      webSocketFactory: () => new SockJS(wsUrl) as any,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      debug: (str) => {
        if (process.env.NODE_ENV === 'development') {
          debugLogger.log('useQRLoginWebSocket', 'STOMP Debug:', str);
        }
      },
      onConnect: () => {
        debugLogger.log('useQRLoginWebSocket', 'Connected to WebSocket');
        isConnectingRef.current = false;
        setIsConnected(true);
        setError(null);

        // Subscribe to QR login status topic
        subscriptionRef.current = client.subscribe(
          `/topic/qr-login/${sessionId}`,
          (message: IMessage) => {
            try {
              const statusMessage: QRStatusMessage = JSON.parse(message.body);
              debugLogger.log('useQRLoginWebSocket', 'Received QR status update', statusMessage);
              
              setLatestStatus(statusMessage);
              callbacksRef.current.onStatusUpdate?.(statusMessage);
            } catch (err) {
              debugLogger.log('useQRLoginWebSocket', 'Failed to parse status message', err);
            }
          }
        );

        // Send subscription message to trigger initial status
        try {
          client.publish({
            destination: `/app/qr-login/subscribe/${sessionId}`,
            body: JSON.stringify({ sessionId })
          });
        } catch (err) {
          debugLogger.log('useQRLoginWebSocket', 'Failed to publish subscription message', err);
        }

        debugLogger.log('useQRLoginWebSocket', 'Subscribed to QR login status', { sessionId });
      },
      onDisconnect: () => {
        debugLogger.log('useQRLoginWebSocket', 'Disconnected from WebSocket');
        isConnectingRef.current = false;
        setIsConnected(false);
      },
      onStompError: (frame) => {
        debugLogger.log('useQRLoginWebSocket', 'STOMP error', frame);
        isConnectingRef.current = false;
        const errorMessage = frame.headers?.message || 'WebSocket connection error';
        setError(errorMessage);
        callbacksRef.current.onError?.(new Error(errorMessage));
      },
      onWebSocketError: (event) => {
        debugLogger.log('useQRLoginWebSocket', 'WebSocket error', event);
        isConnectingRef.current = false;
        setError('WebSocket connection failed');
        callbacksRef.current.onError?.(event);
      }
    });

    clientRef.current = client;
    
    try {
      client.activate();
    } catch (err) {
      debugLogger.log('useQRLoginWebSocket', 'Failed to activate client', err);
      isConnectingRef.current = false;
      setError('Failed to activate WebSocket connection');
    }
  }, [sessionId, isConnected]);

  const disconnect = useCallback(() => {
    isConnectingRef.current = false;
    
    if (subscriptionRef.current) {
      try {
        subscriptionRef.current.unsubscribe();
      } catch (err) {
        debugLogger.log('useQRLoginWebSocket', 'Error unsubscribing', err);
      }
      subscriptionRef.current = null;
    }

    if (clientRef.current) {
      try {
        // Check if client is active before deactivating
        if (clientRef.current.connected || clientRef.current.active) {
          clientRef.current.deactivate();
        }
      } catch (err) {
        debugLogger.log('useQRLoginWebSocket', 'Error deactivating client', err);
      }
      clientRef.current = null;
    }

    currentSessionIdRef.current = undefined;
    setIsConnected(false);
    setError(null);
    debugLogger.log('useQRLoginWebSocket', 'Disconnected and cleaned up');
  }, []);

  // Auto-connect when sessionId is available
  useEffect(() => {
    if (sessionId) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      // Only disconnect if sessionId is actually changing or component is unmounting
      if (!sessionId || currentSessionIdRef.current !== sessionId) {
        disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return {
    isConnected,
    error,
    latestStatus,
    connect,
    disconnect
  };
};
